import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Ruler3D } from 'features/assetViz/objects/decor/Ruler3D'
import { TankDecorGroup3D } from 'features/assetViz/objects/decor/TankDecorGroup3D'
import { HorizontalWeldFeature3D } from 'features/assetViz/objects/features/HorizontalWeldFeature3D'
import { PlateFeature3D } from 'features/assetViz/objects/features/PlateFeature3D'
import { VerticalWeldFeature3D } from 'features/assetViz/objects/features/VerticalWeldFeature3D'
import {
  planeGeometry,
  xWrappedShellGeometry,
} from 'features/assetViz/utils/geometryDefines'
import { extractScale, Vec3 } from 'features/assetViz/utils/helpers'
import React from 'react'
import { Vector3 } from 'three'
import {
  GeckoComponentRect,
  GeckoHoodStraight,
  GeckoHorizontalWeld,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoTubePlate,
  GeckoVerticalWeld,
  TUBESPACING_DEFAULT,
} from 'types'

import { ModelSurface3D } from './ModelSurface3D'
import { Surface3DGroupProps } from './Surface3D'

/**
 * A Rich Asset Model Hood Straight component, plotted on a cartesian plane, with the center of its
 * base at "position" and decorated with Rich Asset features specific to hood straights.
 * @param model               The hood straight component model
 * @param assetModel          The asset model for the asset that the Hood Straight is a part of
 * @param groupProps          Position, rotation, scale, and other 3D properties
 * @constructor
 */
export const HoodStraightSurface3D: React.FC<
  {
    model: GeckoHoodStraight
  } & Surface3DGroupProps
> = ({ model, ...groupProps }) => {
  const { show3D } = useAssetVizInternal()

  const { bounds, scale, verticalWelds, horizontalWelds, plates } =
    React.useMemo(() => {
      const verticalWelds: Array<GeckoVerticalWeld> = model.verticalWeldsArray
      const horizontalWelds: Array<GeckoHorizontalWeld> =
        model.horizontalWeldsArray
      const plates: Array<GeckoTubePlate> = model.platesArray
      const tubeSpacing = model.tubeSpacing ?? TUBESPACING_DEFAULT

      const scale = extractScale(groupProps.scale).multiply(
        show3D
          ? new Vector3(
              model.radius.num,
              model.componentBounds.height.num,
              model.radius.num
            )
          : new Vector3(
              model.componentBounds.width.convertFromTubes(tubeSpacing).num,
              model.componentBounds.height.num,
              1.0
            )
      )

      const adjustedBounds = show3D
        ? GeckoComponentRect.CreateFromXYs(
            model.componentBounds.componentID,
            0, // Start at 0 instead of 1 for proper UV mapping
            model.componentBounds.start.y.num,
            model.componentBounds.width.num - 1,
            model.componentBounds.end.y.num,
            model.componentBounds.x.unit,
            model.componentBounds.y.unit
          )
        : model.componentBounds

      return {
        bounds: adjustedBounds,
        scale: scale,
        verticalWelds: verticalWelds,
        horizontalWelds: horizontalWelds,
        plates: plates,
      }
    }, [model, show3D, groupProps.scale])

  const geometry = React.useMemo(() => {
    if (!show3D) return planeGeometry
    return xWrappedShellGeometry
  }, [show3D])

  const positionXTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relX = coord
        .convertTo(bounds.start.x.unit)
        .minus(bounds.start.x)
        .scale(1.0 / bounds.width.num).num
      if (!show3D) return [relX, 0, 0]
      const angle = 2.0 * Math.PI * (relX - 0.25) // rotated by 90 degrees in 3D mode
      return [Math.sin(angle), 0, Math.cos(angle)]
    },
    [show3D, bounds]
  )

  const positionYTick = React.useCallback<(coord: GeckoMeasurement) => Vec3>(
    (coord) => {
      const relY = coord
        .convertTo(GeckoMeasurementUnit.Meters)
        .minus(bounds.start.y)
        .scale(1.0 / bounds.height.num).num
      if (!show3D) return [0, relY, 0]
      return [-0.875, relY, 0.5]
    },
    [show3D, bounds]
  )

  //----------------------------------------------------------------------------

  return (
    <ModelSurface3D
      geometry={geometry}
      bounds={bounds}
      componentModel={model}
      {...groupProps}
      scale={scale}>
      <TankDecorGroup3D yPivot={show3D}>
        {/* Y ruler -------------------------------------------------------- */}
        <Ruler3D
          anchor={'bottom'}
          start={model.componentBounds.start.y}
          end={model.componentBounds.end.y}
          includeTicks
          includeStart
          includeEnd
          getTickPosition={positionYTick}
        />
      </TankDecorGroup3D>
      {/* X ruler ---------------------------------------------------------- */}
      <TankDecorGroup3D>
        <Ruler3D
          anchor={'right'}
          start={model.componentBounds.start.x}
          end={model.componentBounds.end.x}
          includeTicks
          includeStart
          includeEnd={!show3D}
          getTickPosition={positionXTick}
        />
      </TankDecorGroup3D>
      {verticalWelds.map((weld) => (
        <VerticalWeldFeature3D key={weld.id} model={weld} />
      ))}
      {horizontalWelds.map((weld) => (
        <HorizontalWeldFeature3D key={weld.id} model={weld} />
      ))}
      {plates.map((plate) => (
        <PlateFeature3D key={plate.id} model={plate} />
      ))}
    </ModelSurface3D>
  )
}
